import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { toast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Wrench, Building2, Globe, SlidersHorizontal, Loader2, Users, User, CreditCard, Gift, Briefcase, Newspaper, Bot, Video, ClipboardCheck, Search, Layers, Lightbulb, FlaskConical, BarChart3, Settings2, Megaphone, Rss, PhoneCall } from 'lucide-react';
import { useAIToolsCatalog, useCreateTool, useUpdateTool, useDeleteTool, type AIToolCatalogRow } from '@/hooks/useAIToolsCatalog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWebinarRedirect, useUpdateWebinarRedirect } from '@/hooks/useWebinarRedirect';

import ReferralCodesTab from '@/components/admin/config/ReferralCodesTab';
import ReferralsTrackingTab from '@/components/admin/config/ReferralsTrackingTab';


import NewsCategoriesTab from '@/components/admin/config/NewsCategoriesTab';
import ContentCategoriesTab from '@/components/admin/config/ContentCategoriesTab';
import ToolCategoriesTab from '@/components/admin/config/ToolCategoriesTab';
import LiaConfigTab from '@/components/admin/config/LiaConfigTab';
import VimeoConfigTab from '@/components/admin/config/VimeoConfigTab';
import EvaluationConfigTab from '@/components/admin/config/EvaluationConfigTab';
import DiagnosticConfigTab from '@/components/admin/config/DiagnosticConfigTab';
import ParticipantDiagnosticConfigTab from '@/components/admin/config/ParticipantDiagnosticConfigTab';
import PaymentGatewayConfigTab from '@/components/admin/config/PaymentGatewayConfigTab';
import CompetenciesTab from '@/components/admin/config/CompetenciesTab';
import PrototypeAssistantConfigTab from '@/components/admin/config/PrototypeAssistantConfigTab';
import ChallengeAssistantConfigTab from '@/components/admin/config/ChallengeAssistantConfigTab';
import GoogleAnalyticsConfigTab from '@/components/admin/config/GoogleAnalyticsConfigTab';
import AnnouncementBannerTab from '@/components/admin/config/AnnouncementBannerTab';
import WebTrendsConfigTab from '@/components/admin/config/WebTrendsConfigTab';
import SalesLeadsConfigTab from '@/components/admin/config/SalesLeadsConfigTab';
import SupportTicketsConfigTab from '@/components/admin/config/SupportTicketsConfigTab';

// --- Types for areas & sectors ---
interface BusinessArea {
  id: string;
  name: string;
  icon: string | null;
}

interface Sector {
  id: string;
  name: string;
}

// --- Hooks for areas ---
function useBusinessAreas() {
  return useQuery({
    queryKey: ['business_areas'],
    queryFn: async () => {
      const { data, error } = await supabase.from('business_areas').select('*').order('name');
      if (error) throw error;
      return data as BusinessArea[];
    },
  });
}

function useIndustrySectors() {
  return useQuery({
    queryKey: ['industry_sectors'],
    queryFn: async () => {
      const { data, error } = await supabase.from('industry_sectors').select('*').order('name');
      if (error) throw error;
      return data as Sector[];
    },
  });
}

// --- Tool categories from DB ---
interface ToolCategory {
  id: string;
  key: string;
  name: string;
  sort_order: number;
}

function useToolCategories() {
  return useQuery({
    queryKey: ['ai_tool_categories'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ai_tool_categories').select('*').order('sort_order');
      if (error) throw error;
      return data as ToolCategory[];
    },
  });
}

const pricingOptions = [
  { value: 'free', label: 'Gratis' },
  { value: 'freemium', label: 'Freemium' },
  { value: 'paid', label: 'De pago' },
];

// Helper to split comma-separated string into array
const csvToArray = (s: string): string[] => s ? s.split(',').map(v => v.trim()).filter(Boolean) : [];
const arrayToCsv = (a: string[] | null | undefined): string => (a ?? []).join(', ');

interface ToolFormState {
  name: string;
  category: string;
  vendor: string;
  description: string;
  full_description: string;
  website_url: string;
  logo_url: string;
  rating: string;
  pricing: string;
  trending: boolean;
  featured: boolean;
  company: string;
  founded: string;
  level: string;
  
  tags: string;
  use_cases: string;
  pros: string;
  cons: string;
  integrations: string;
}

const emptyToolForm: ToolFormState = {
  name: '', category: '', vendor: '', description: '', full_description: '',
  website_url: '', logo_url: '', rating: '', pricing: 'paid', trending: false,
  featured: false, company: '', founded: '', level: '0', tags: '',
  use_cases: '', pros: '', cons: '', integrations: '',
};

function toolToForm(item: AIToolCatalogRow): ToolFormState {
  return {
    name: item.name,
    category: item.category || '',
    vendor: item.vendor || '',
    description: item.description || '',
    full_description: item.full_description || '',
    website_url: item.website_url || '',
    logo_url: item.logo_url || '',
    rating: item.rating != null ? String(item.rating) : '',
    pricing: item.pricing || 'paid',
    trending: item.trending ?? false,
    featured: item.featured ?? false,
    company: item.company || '',
    founded: item.founded || '',
    level: item.level != null ? String(item.level) : '0',
    
    tags: arrayToCsv(item.tags),
    use_cases: arrayToCsv(item.use_cases),
    pros: arrayToCsv(item.pros),
    cons: arrayToCsv(item.cons),
    integrations: arrayToCsv(item.integrations),
  };
}

export default function PlatformConfigPage() {
  const qc = useQueryClient();

  // --- General settings ---
  const { data: maxFreeProjectsSetting } = useQuery({
    queryKey: ['platform-setting', 'max_free_projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'max_free_projects')
        .maybeSingle();
      if (error) throw error;
      const raw = data?.value;
      return typeof raw === 'number' ? raw : Number(raw) || 1;
    },
  });
  const [maxFreeProjects, setMaxFreeProjects] = useState<number>(1);
  const [maxFreeProjectsDirty, setMaxFreeProjectsDirty] = useState(false);

  // --- Webinar redirect ---
  const { data: webinarConfig } = useWebinarRedirect();
  const saveWebinar = useUpdateWebinarRedirect();
  const [webinarEnabled, setWebinarEnabled] = useState(false);
  const [webinarUrl, setWebinarUrl] = useState('');
  const [webinarDirty, setWebinarDirty] = useState(false);

  useEffect(() => {
    if (webinarConfig && !webinarDirty) {
      setWebinarEnabled(webinarConfig.enabled);
      setWebinarUrl(webinarConfig.url);
    }
  }, [webinarConfig]);

  useEffect(() => {
    if (maxFreeProjectsSetting !== undefined && !maxFreeProjectsDirty) {
      setMaxFreeProjects(maxFreeProjectsSetting);
    }
  }, [maxFreeProjectsSetting]);

  const saveMaxFreeProjects = useMutation({
    mutationFn: async (val: number) => {
      const { error } = await supabase
        .from('platform_settings')
        .update({ value: val as any })
        .eq('key', 'max_free_projects');
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-setting', 'max_free_projects'] });
      setMaxFreeProjectsDirty(false);
      toast({ title: 'Límite de proyectos actualizado' });
    },
    onError: () => {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    },
  });

  // --- Public routes (Rutas) visibility ---
  const { data: publicRoutesEnabledSetting } = useQuery({
    queryKey: ['platform-setting', 'public_routes_enabled'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'public_routes_enabled')
        .maybeSingle();
      if (error) throw error;
      const raw = data?.value;
      if (raw === false || raw === 'false') return false;
      return true;
    },
  });
  const savePublicRoutesEnabled = useMutation({
    mutationFn: async (val: boolean) => {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({ key: 'public_routes_enabled', value: val as any }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-setting', 'public_routes_enabled'] });
      toast({ title: 'Visibilidad de Rutas actualizada' });
    },
    onError: () => toast({ title: 'Error al guardar', variant: 'destructive' }),
  });

  // --- Tools from DB ---
  const { data: tools = [], isLoading: toolsLoading } = useAIToolsCatalog();
  const createTool = useCreateTool();
  const updateTool = useUpdateTool();
  const deleteTool = useDeleteTool();

  // --- Tool categories from DB ---
  const { data: toolCategories = [] } = useToolCategories();
  const catLabelMap = Object.fromEntries(toolCategories.map(c => [c.key, c.name]));

  // --- Areas from DB ---
  const { data: areas = [], isLoading: areasLoading } = useBusinessAreas();

  const createArea = useMutation({
    mutationFn: async (input: { name: string; icon: string }) => {
      const { error } = await supabase.from('business_areas').insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business_areas'] }),
  });
  const updateArea = useMutation({
    mutationFn: async ({ id, ...input }: { id: string; name: string; icon: string }) => {
      const { error } = await supabase.from('business_areas').update(input).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business_areas'] }),
  });
  const deleteArea = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('business_areas').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business_areas'] }),
  });

  // --- Sectors from DB ---
  const { data: sectors = [], isLoading: sectorsLoading } = useIndustrySectors();

  const createSector = useMutation({
    mutationFn: async (input: { name: string }) => {
      const { error } = await supabase.from('industry_sectors').insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['industry_sectors'] }),
  });
  const updateSector = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase.from('industry_sectors').update({ name }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['industry_sectors'] }),
  });
  const deleteSector = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('industry_sectors').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['industry_sectors'] }),
  });

  // Search & filter state
  const [toolSearch, setToolSearch] = useState('');
  const [toolCatFilter, setToolCatFilter] = useState('__all__');
  const [toolPricingFilter, setToolPricingFilter] = useState('__all__');
  const [areaSearch, setAreaSearch] = useState('');
  const [sectorSearch, setSectorSearch] = useState('');

  // Dialog state
  const [toolDialog, setToolDialog] = useState<{ open: boolean; item?: AIToolCatalogRow }>({ open: false });
  const [areaDialog, setAreaDialog] = useState<{ open: boolean; item?: BusinessArea }>({ open: false });
  const [sectorDialog, setSectorDialog] = useState<{ open: boolean; item?: Sector }>({ open: false });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: string; id: string }>({ open: false, type: '', id: '' });

  // Form state
  const [toolForm, setToolForm] = useState<ToolFormState>(emptyToolForm);
  const [areaForm, setAreaForm] = useState({ name: '', icon: '' });
  const [sectorForm, setSectorForm] = useState({ name: '' });

  // Filtered data
  const filteredTools = tools.filter(t => {
    const q = toolSearch.toLowerCase();
    if (q && !t.name.toLowerCase().includes(q) && !(t.vendor || '').toLowerCase().includes(q)) return false;
    if (toolCatFilter !== '__all__' && t.category !== toolCatFilter) return false;
    if (toolPricingFilter !== '__all__' && t.pricing !== toolPricingFilter) return false;
    return true;
  });
  const filteredAreas = areas.filter(a => !areaSearch || a.name.toLowerCase().includes(areaSearch.toLowerCase()));
  const filteredSectors = sectors.filter(s => !sectorSearch || s.name.toLowerCase().includes(sectorSearch.toLowerCase()));

  // --- Tool handlers ---
  const openToolDialog = (item?: AIToolCatalogRow) => {
    setToolForm(item ? toolToForm(item) : { ...emptyToolForm });
    setToolDialog({ open: true, item });
  };

  const saveTool = async () => {
    if (!toolForm.name || !toolForm.category) return;
    const payload = {
      name: toolForm.name,
      category: toolForm.category,
      vendor: toolForm.vendor || undefined,
      description: toolForm.description || undefined,
      full_description: toolForm.full_description || undefined,
      website_url: toolForm.website_url || undefined,
      logo_url: toolForm.logo_url || undefined,
      rating: toolForm.rating ? Number(toolForm.rating) : undefined,
      pricing: toolForm.pricing || undefined,
      trending: toolForm.trending,
      featured: toolForm.featured,
      company: toolForm.company || undefined,
      founded: toolForm.founded || undefined,
      level: toolForm.level !== '' ? Number(toolForm.level) : 0,
      
      tags: csvToArray(toolForm.tags),
      use_cases: csvToArray(toolForm.use_cases),
      pros: csvToArray(toolForm.pros),
      cons: csvToArray(toolForm.cons),
      integrations: csvToArray(toolForm.integrations),
    };
    try {
      if (toolDialog.item) {
        await updateTool.mutateAsync({ id: toolDialog.item.id, ...payload });
      } else {
        await createTool.mutateAsync(payload);
      }
      setToolDialog({ open: false });
      toast({ title: toolDialog.item ? 'Herramienta actualizada' : 'Herramienta agregada' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  };

  // --- Area handlers ---
  const openAreaDialog = (item?: BusinessArea) => {
    setAreaForm(item ? { name: item.name, icon: item.icon || '' } : { name: '', icon: '' });
    setAreaDialog({ open: true, item });
  };

  const saveArea = async () => {
    if (!areaForm.name) return;
    try {
      if (areaDialog.item) {
        await updateArea.mutateAsync({ id: areaDialog.item.id, ...areaForm });
      } else {
        await createArea.mutateAsync(areaForm);
      }
      setAreaDialog({ open: false });
      toast({ title: areaDialog.item ? 'Área actualizada' : 'Área agregada' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  };

  // --- Sector handlers ---
  const openSectorDialog = (item?: Sector) => {
    setSectorForm(item ? { name: item.name } : { name: '' });
    setSectorDialog({ open: true, item });
  };

  const saveSector = async () => {
    if (!sectorForm.name) return;
    try {
      if (sectorDialog.item) {
        await updateSector.mutateAsync({ id: sectorDialog.item.id, name: sectorForm.name });
      } else {
        await createSector.mutateAsync({ name: sectorForm.name });
      }
      setSectorDialog({ open: false });
      toast({ title: sectorDialog.item ? 'Sector actualizado' : 'Sector agregado' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  };

  // --- Delete handler ---
  const confirmDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      if (type === 'tool') await deleteTool.mutateAsync(id);
      if (type === 'area') await deleteArea.mutateAsync(id);
      if (type === 'sector') await deleteSector.mutateAsync(id);
      setDeleteDialog({ open: false, type: '', id: '' });
      toast({ title: 'Elemento eliminado' });
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const tf = (field: keyof ToolFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setToolForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <SlidersHorizontal className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Configuración de Plataforma</h1>
          <p className="text-muted-foreground">Parámetros globales que aplican a toda la plataforma</p>
        </div>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
          <TabsTrigger value="general" className="gap-2"><Settings2 className="w-4 h-4" />General</TabsTrigger>
          <TabsTrigger value="tools" className="gap-2"><Wrench className="w-4 h-4" />Herramientas IA</TabsTrigger>
          <TabsTrigger value="areas" className="gap-2"><Building2 className="w-4 h-4" />Áreas de Negocio</TabsTrigger>
          <TabsTrigger value="sectors" className="gap-2"><Globe className="w-4 h-4" />Sectores</TabsTrigger>
          <TabsTrigger value="competencies" className="gap-2"><Briefcase className="w-4 h-4" />Competencias</TabsTrigger>
          <TabsTrigger value="referrals" className="gap-2"><Gift className="w-4 h-4" />Referidos</TabsTrigger>
          <TabsTrigger value="news-categories" className="gap-2"><Newspaper className="w-4 h-4" />Cat. Noticias</TabsTrigger>
          <TabsTrigger value="tool-categories" className="gap-2"><Wrench className="w-4 h-4" />Cat. Herramientas</TabsTrigger>
          <TabsTrigger value="content-categories" className="gap-2"><Layers className="w-4 h-4" />Cat. Contenido</TabsTrigger>
          <TabsTrigger value="lia" className="gap-2"><Bot className="w-4 h-4" />Asistente LIA</TabsTrigger>
          <TabsTrigger value="vimeo" className="gap-2"><Video className="w-4 h-4" />Vimeo</TabsTrigger>
          <TabsTrigger value="evaluation" className="gap-2"><ClipboardCheck className="w-4 h-4" />Evaluación IA</TabsTrigger>
          <TabsTrigger value="diagnostic" className="gap-2"><Building2 className="w-4 h-4" />Diagnóstico Empresarial IA</TabsTrigger>
          <TabsTrigger value="participant-diagnostic" className="gap-2"><User className="w-4 h-4" />Diagnóstico Participante IA</TabsTrigger>
          <TabsTrigger value="payments" className="gap-2"><CreditCard className="w-4 h-4" />Pasarela de Pagos</TabsTrigger>
          <TabsTrigger value="prototype-assistant" className="gap-2"><Lightbulb className="w-4 h-4" />Asistente Prototipo</TabsTrigger>
          <TabsTrigger value="challenge-assistant" className="gap-2"><FlaskConical className="w-4 h-4" />Asistente Lab Retos</TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2"><BarChart3 className="w-4 h-4" />Analytics</TabsTrigger>
          <TabsTrigger value="announcement" className="gap-2"><Megaphone className="w-4 h-4" />Anuncio Header</TabsTrigger>
          <TabsTrigger value="web-trends" className="gap-2"><Rss className="w-4 h-4" />Tendencias Web</TabsTrigger>
          <TabsTrigger value="sales-leads" className="gap-2"><PhoneCall className="w-4 h-4" />Prospectos de Venta</TabsTrigger>
          <TabsTrigger value="support-tickets" className="gap-2"><PhoneCall className="w-4 h-4" />Soporte</TabsTrigger>
        </TabsList>

        {/* === GENERAL === */}
        <TabsContent value="general" className="space-y-6">
          <div className="max-w-md space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Límites de Plan Gratuito</h3>
              <p className="text-sm text-muted-foreground mb-4">Configura las restricciones para usuarios sin plan pago.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="max-free-projects">Máximo de proyectos para usuarios gratuitos</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="max-free-projects"
                  type="number"
                  min={0}
                  max={100}
                  value={maxFreeProjects}
                  onChange={(e) => {
                    setMaxFreeProjects(Number(e.target.value));
                    setMaxFreeProjectsDirty(true);
                  }}
                  className="w-24"
                />
                <Button
                  size="sm"
                  disabled={!maxFreeProjectsDirty || saveMaxFreeProjects.isPending}
                  onClick={() => saveMaxFreeProjects.mutate(maxFreeProjects)}
                >
                  {saveMaxFreeProjects.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Guardar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Los usuarios con plan pago, suscripción activa o vinculados a una organización no tienen límite.
              </p>
            </div>
          </div>

          <div className="max-w-md space-y-4 pt-6 border-t border-border">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Visibilidad pública de Rutas</h3>
              <p className="text-sm text-muted-foreground mb-4">Si está desactivado, las Rutas no aparecerán en el menú de visitantes ni de participantes.</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="public-routes-enabled"
                checked={publicRoutesEnabledSetting ?? true}
                disabled={savePublicRoutesEnabled.isPending}
                onCheckedChange={(val) => savePublicRoutesEnabled.mutate(val)}
              />
              <Label htmlFor="public-routes-enabled">Acceso público a Rutas</Label>
            </div>
          </div>

          <div className="max-w-md space-y-4 pt-6 border-t border-border">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Redirección /webinar</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Cualquier persona que entre a <span className="font-medium">lideria.org/webinar</span> será llevada a la dirección que configures aquí.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="webinar-enabled"
                checked={webinarEnabled}
                onCheckedChange={(val) => { setWebinarEnabled(val); setWebinarDirty(true); }}
              />
              <Label htmlFor="webinar-enabled">Redirección activa</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="webinar-url">Dirección de destino</Label>
              <Input
                id="webinar-url"
                type="url"
                placeholder="https://..."
                value={webinarUrl}
                onChange={(e) => { setWebinarUrl(e.target.value); setWebinarDirty(true); }}
              />
              <p className="text-xs text-muted-foreground">Debe empezar por http:// o https://</p>
            </div>
            <Button
              size="sm"
              disabled={!webinarDirty || saveWebinar.isPending}
              onClick={() => saveWebinar.mutate({ enabled: webinarEnabled, url: webinarUrl.trim() }, { onSuccess: () => setWebinarDirty(false) })}
            >
              {saveWebinar.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Guardar
            </Button>
          </div>
        </TabsContent>

        {/* === HERRAMIENTAS IA === */}
        <TabsContent value="tools" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre o vendor…" value={toolSearch} onChange={e => setToolSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={toolCatFilter} onValueChange={setToolCatFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas las categorías</SelectItem>
                {toolCategories.map(c => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={toolPricingFilter} onValueChange={setToolPricingFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Pricing" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todo pricing</SelectItem>
                {pricingOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => openToolDialog()} size="sm"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
          </div>
          <p className="text-xs text-muted-foreground">{filteredTools.length} de {tools.length} herramientas</p>
          {toolsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <div className="rounded-xl border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Pricing</TableHead>
                    <TableHead className="w-24">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTools.map(tool => (
                    <TableRow key={tool.id}>
                      <TableCell className="font-medium">{tool.name}</TableCell>
                      <TableCell>{catLabelMap[tool.category || ''] || tool.category}</TableCell>
                      <TableCell>{tool.vendor}</TableCell>
                      <TableCell className="capitalize">{tool.pricing || '—'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openToolDialog(tool)}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, type: 'tool', id: tool.id })}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* === ÁREAS DE NEGOCIO === */}
        <TabsContent value="areas" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar área…" value={areaSearch} onChange={e => setAreaSearch(e.target.value)} className="pl-9" />
            </div>
            <Button onClick={() => openAreaDialog()} size="sm"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
          </div>
          <p className="text-xs text-muted-foreground">{filteredAreas.length} de {areas.length} áreas</p>
          {areasLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <div className="rounded-xl border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Icono</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="w-24">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAreas.map(area => (
                    <TableRow key={area.id}>
                      <TableCell className="text-xl">{area.icon}</TableCell>
                      <TableCell className="font-medium">{area.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openAreaDialog(area)}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, type: 'area', id: area.id })}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* === SECTORES / INDUSTRIAS === */}
        <TabsContent value="sectors" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar sector…" value={sectorSearch} onChange={e => setSectorSearch(e.target.value)} className="pl-9" />
            </div>
            <Button onClick={() => openSectorDialog()} size="sm"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
          </div>
          <p className="text-xs text-muted-foreground">{filteredSectors.length} de {sectors.length} sectores</p>
          {sectorsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <div className="rounded-xl border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="w-24">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSectors.map(sector => (
                    <TableRow key={sector.id}>
                      <TableCell className="font-medium">{sector.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openSectorDialog(sector)}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, type: 'sector', id: sector.id })}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>



        

        {/* === COMPETENCIAS === */}
        <TabsContent value="competencies"><CompetenciesTab /></TabsContent>

        {/* === CÓDIGOS DE REFERIDOS === */}
        <TabsContent value="referrals">
          <Tabs defaultValue="tracking" className="space-y-4">
            <TabsList>
              <TabsTrigger value="tracking">Seguimiento</TabsTrigger>
              <TabsTrigger value="codes">Códigos</TabsTrigger>
            </TabsList>
            <TabsContent value="tracking"><ReferralsTrackingTab /></TabsContent>
            <TabsContent value="codes"><ReferralCodesTab /></TabsContent>
          </Tabs>
        </TabsContent>


        {/* === CATEGORÍAS DE NOTICIAS === */}
        <TabsContent value="news-categories"><NewsCategoriesTab /></TabsContent>

        {/* === CATEGORÍAS DE HERRAMIENTAS === */}
        <TabsContent value="tool-categories"><ToolCategoriesTab /></TabsContent>

        {/* Content Categories */}
        <TabsContent value="content-categories"><ContentCategoriesTab /></TabsContent>

        {/* === ASISTENTE LIA === */}
        <TabsContent value="lia"><LiaConfigTab /></TabsContent>

        {/* === VIMEO === */}
        <TabsContent value="vimeo"><VimeoConfigTab /></TabsContent>

        {/* === EVALUACIÓN IA (Proyectos) === */}
        <TabsContent value="evaluation"><EvaluationConfigTab /></TabsContent>

        {/* === DIAGNÓSTICO EMPRESARIAL IA === */}
        <TabsContent value="diagnostic"><DiagnosticConfigTab /></TabsContent>

        {/* === DIAGNÓSTICO PARTICIPANTE IA === */}
        <TabsContent value="participant-diagnostic"><ParticipantDiagnosticConfigTab /></TabsContent>

        {/* === PASARELA DE PAGOS === */}
        <TabsContent value="payments"><PaymentGatewayConfigTab /></TabsContent>

        {/* === ASISTENTE PROTOTIPO === */}
        <TabsContent value="prototype-assistant"><PrototypeAssistantConfigTab /></TabsContent>
        <TabsContent value="challenge-assistant"><ChallengeAssistantConfigTab /></TabsContent>
        <TabsContent value="analytics"><GoogleAnalyticsConfigTab /></TabsContent>
        <TabsContent value="announcement"><AnnouncementBannerTab /></TabsContent>
        <TabsContent value="web-trends"><WebTrendsConfigTab /></TabsContent>
        <TabsContent value="sales-leads"><SalesLeadsConfigTab /></TabsContent>
        <TabsContent value="support-tickets"><SupportTicketsConfigTab /></TabsContent>
      </Tabs>

      {/* === DIALOGS === */}

      {/* Tool Dialog - Expanded */}
      <Dialog open={toolDialog.open} onOpenChange={(open) => !open && setToolDialog({ open: false })}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{toolDialog.item ? 'Editar Herramienta' : 'Agregar Herramienta'}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-4">
            <div className="space-y-4 pb-2">
              {/* Row 1: Name + Category */}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nombre *</Label><Input value={toolForm.name} onChange={tf('name')} placeholder="Ej: ChatGPT" /></div>
                <div>
                  <Label>Categoría *</Label>
                  <Select value={toolForm.category} onValueChange={v => setToolForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{toolCategories.map(c => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Vendor + Company */}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Vendor</Label><Input value={toolForm.vendor} onChange={tf('vendor')} placeholder="Ej: OpenAI" /></div>
                <div><Label>Compañía</Label><Input value={toolForm.company} onChange={tf('company')} placeholder="Ej: OpenAI Inc." /></div>
              </div>

              {/* Description */}
              <div><Label>Descripción corta</Label><Textarea value={toolForm.description} onChange={tf('description')} placeholder="Breve descripción de la herramienta" rows={2} /></div>

              {/* Full description */}
              <div><Label>Descripción completa</Label><Textarea value={toolForm.full_description} onChange={tf('full_description')} placeholder="Descripción detallada para la ficha de la herramienta" rows={4} /></div>

              {/* Row: URLs */}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Sitio web</Label><Input value={toolForm.website_url} onChange={tf('website_url')} placeholder="https://..." /></div>
                <div><Label>URL del logo</Label><Input value={toolForm.logo_url} onChange={tf('logo_url')} placeholder="https://..." /></div>
              </div>

              {/* Row: Rating + Pricing + Founded + Level */}
              <div className="grid grid-cols-4 gap-4">
                <div><Label>Rating (0-5)</Label><Input type="number" min="0" max="5" step="0.1" value={toolForm.rating} onChange={tf('rating')} placeholder="4.5" /></div>
                <div>
                  <Label>Pricing</Label>
                  <Select value={toolForm.pricing} onValueChange={v => setToolForm(f => ({ ...f, pricing: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{pricingOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Fundada</Label><Input value={toolForm.founded} onChange={tf('founded')} placeholder="Ej: 2015" /></div>
                <div>
                  <Label>Nivel mínimo</Label>
                  <Select value={toolForm.level} onValueChange={v => setToolForm(f => ({ ...f, level: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Switches */}
              <div className="flex gap-8">
                <div className="flex items-center gap-2">
                  <Switch checked={toolForm.trending} onCheckedChange={v => setToolForm(f => ({ ...f, trending: v }))} />
                  <Label>Trending</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={toolForm.featured} onCheckedChange={v => setToolForm(f => ({ ...f, featured: v }))} />
                  <Label>Destacada</Label>
                </div>
              </div>

              {/* Comma-separated fields */}
              
              <div><Label>Tags (separados por coma)</Label><Input value={toolForm.tags} onChange={tf('tags')} placeholder="chatbot, nlp, productividad" /></div>
              <div><Label>Casos de uso (separados por coma)</Label><Input value={toolForm.use_cases} onChange={tf('use_cases')} placeholder="Atención al cliente, Redacción, Análisis" /></div>
              <div><Label>Pros (separados por coma)</Label><Input value={toolForm.pros} onChange={tf('pros')} placeholder="Fácil de usar, Rápido, Preciso" /></div>
              <div><Label>Contras (separados por coma)</Label><Input value={toolForm.cons} onChange={tf('cons')} placeholder="Costo alto, Curva de aprendizaje" /></div>
              <div><Label>Integraciones (separadas por coma)</Label><Input value={toolForm.integrations} onChange={tf('integrations')} placeholder="Slack, Teams, Zapier" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToolDialog({ open: false })}>Cancelar</Button>
            <Button onClick={saveTool} disabled={!toolForm.name || !toolForm.category}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Area Dialog */}
      <Dialog open={areaDialog.open} onOpenChange={(open) => !open && setAreaDialog({ open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{areaDialog.item ? 'Editar Área' : 'Agregar Área'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={areaForm.name} onChange={e => setAreaForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Marketing" /></div>
            <div><Label>Icono (emoji)</Label><Input value={areaForm.icon} onChange={e => setAreaForm(f => ({ ...f, icon: e.target.value }))} placeholder="Ej: 📢" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAreaDialog({ open: false })}>Cancelar</Button>
            <Button onClick={saveArea} disabled={!areaForm.name}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sector Dialog */}
      <Dialog open={sectorDialog.open} onOpenChange={(open) => !open && setSectorDialog({ open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{sectorDialog.item ? 'Editar Sector' : 'Agregar Sector'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={sectorForm.name} onChange={e => setSectorForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Tecnología" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectorDialog({ open: false })}>Cancelar</Button>
            <Button onClick={saveSector} disabled={!sectorForm.name}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: '', id: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este elemento?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
